import {Trans} from '@lingui/macro';
import _, {isUndefined} from 'lodash';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {
  conferenceTypeToIcon,
  conferenceTypeToText,
  mapIntegrationTypeToConferenceType,
} from '../../../../../helpers/models/conferenceHelpers';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {ConferenceTypesEnum} from '../../../../../models/api/conferenceModel';
import {getConferencingIntegrations} from '../../../../../store/data/dataSelectors';

/*
 * Constant.
 */

const defaultNullDisplayName = <Trans id="default-video-conferencing-null-name">No default</Trans>;

/*
 * Props.
 */

interface DefaultVideoConferencingDropdownProps {
  conferenceType: ConferenceTypesEnum | undefined;
  supportedProviderConferenceTypes: ReadonlyArray<ConferenceTypesEnum>;
  onConferenceTypeChange: HandlerOf<ConferenceTypesEnum | undefined>;
  className?: string;
}

interface ItemContentProps {
  $isValue?: boolean;
}

/*
 * Styles.
 */

const StyledItemContentDiv = styled.div<ItemContentProps>`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: auto;
  grid-column-gap: ${(p) => (p.$isValue ? 8 : 10)}px;
  justify-content: left;
  align-items: center;
`;

/*
 * Component.
 */

export const DefaultVideoConferencingDropdown: FC<DefaultVideoConferencingDropdownProps> = (props) => {
  const {onConferenceTypeChange, supportedProviderConferenceTypes} = props;
  const conferencingIntegrations = useAppSelector(getConferencingIntegrations);
  const availableConferenceTypes = useMemo(
    () =>
      _(conferencingIntegrations)
        .map((i) => mapIntegrationTypeToConferenceType(i.type))
        .compact()
        .concat(supportedProviderConferenceTypes)
        .value(),
    [conferencingIntegrations, supportedProviderConferenceTypes],
  );

  const renderDropdown = (popoverProps: PopoverTransferProps) => (
    <SimpleDropdown {...popoverProps}>
      <SimpleDropdownItem
        key="default-video-conferencing-item-none"
        onSelect={() => onConferenceTypeChange(undefined)}
        isChecked={isUndefined(props.conferenceType)}
        shouldReserveRightIcon
      >
        {renderItem(undefined)}
      </SimpleDropdownItem>
      {availableConferenceTypes.map((conferenceType) => (
        <SimpleDropdownItem
          key={`default-video-conferencing-item-${conferenceType}`}
          onSelect={() => onConferenceTypeChange(conferenceType)}
          isChecked={props.conferenceType === conferenceType}
          shouldReserveRightIcon
        >
          {renderItem(conferenceType)}
        </SimpleDropdownItem>
      ))}
    </SimpleDropdown>
  );

  return (
    <DropdownBoxCoordinator
      value={renderItem(props.conferenceType, true)}
      renderDropdown={renderDropdown}
      boxClassName={props.className}
      shouldDisablePadding
      noInputMarginRight
    />
  );
};

function renderItem(conferenceType: ConferenceTypesEnum | undefined, isValue?: boolean) {
  if (!conferenceType) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledItemContentDiv $isValue={isValue}>
            {!isValue && <Icon name="none" color={theme.greys.shade70} />}
            {defaultNullDisplayName}
          </StyledItemContentDiv>
        )}
      </UserThemeConsumer>
    );
  }

  return (
    <StyledItemContentDiv $isValue={isValue}>
      {conferenceTypeToIcon[conferenceType]}
      {conferenceTypeToText[conferenceType]}
    </StyledItemContentDiv>
  );
}
